/** Locale-aware formatting for the catalog. Locale follows the active i18next language. */

import { getIntlLocale } from '@/shared/i18n';

/** Format a money amount in RUB, or an em dash when absent. */
export function formatPrice(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(getIntlLocale(), {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a ratio already expressed as a percentage (pass `34.7` to render "34,7", not `0.347`) —
 * margin/markup on the price editor and the prices drawer's cost line (design spec §8.4/§8.3) share
 * this one fixed-precision formatter rather than each re-implementing `Intl.NumberFormat`. Returns
 * the bare NUMBER, no "%" glyph: every caller's own i18n copy already carries the literal " %"
 * around its `{{margin}}`/`{{markup}}` placeholder.
 */
export function formatPercent(value?: number | null, fractionDigits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(getIntlLocale(), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** Format a plain count (thousands separated). */
export function formatNumber(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(getIntlLocale()).format(value);
}

/** Format a stock quantity (up to 3 fractional digits — mirrors `features/inventory`'s own). */
export function formatQuantity(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(getIntlLocale(), { maximumFractionDigits: 3 }).format(value);
}

/** Format an ISO timestamp, or an em dash when absent/invalid. */
export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

/** Human-readable byte size. */
export function formatBytes(bytes?: number | null): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}
